import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getRecentAudit } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
  History, Ship, Package, DollarSign, FileText, User,
  Edit, Plus, Trash2, Check, AlertCircle, RefreshCw,
  Search, Filter, Loader2, ExternalLink
} from 'lucide-react';

const ACTION_ICONS = {
  created: Plus,
  updated: Edit,
  deleted: Trash2,
  approved: Check,
  rejected: AlertCircle,
  registered: User,
  status_changed: RefreshCw,
  bulk_updated: Edit,
  mbl_changed: RefreshCw,
};

const ENTITY_ICONS = {
  shipment: Ship,
  container: Package,
  cost: DollarSign,
  event: FileText,
  note: FileText,
  user: User,
};

const ACTION_COLORS = {
  created: 'bg-green-500/20 text-green-400 border-green-500/30',
  updated: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  deleted: 'bg-red-500/20 text-red-400 border-red-500/30',
  bulk_updated: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  mbl_changed: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const ACTION_LABELS = {
  created: 'Creado',
  updated: 'Actualizado',
  deleted: 'Eliminado',
  bulk_updated: 'Masivo',
  mbl_changed: 'MBL Cambiado',
  status_changed: 'Estatus',
};

const ENTITY_LABELS = {
  shipment: 'Embarque',
  container: 'Contenedor',
  cost: 'Costos',
  event: 'Evento',
  note: 'Nota',
  user: 'Usuario',
};

export default function AuditPage() {
  const { t } = useI18n();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRecentAudit(200);
      setLogs(res.data);
    } catch (err) {
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredLogs = logs.filter(log => {
    if (filterEntity !== 'all' && log.entity_type !== filterEntity) return false;
    if (filterAction !== 'all' && log.action !== filterAction) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        log.description?.toLowerCase().includes(s) ||
        log.entity_id?.toLowerCase().includes(s) ||
        log.user_email?.toLowerCase().includes(s) ||
        log.user_name?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const formatChanges = (changes) => {
    if (!changes) return null;
    return Object.entries(changes).map(([field, value]) => {
      if (typeof value === 'object' && value !== null && 'old' in value && 'new' in value) {
        return (
          <div key={field} className="text-xs text-muted-foreground inline-flex items-center gap-1 mr-3">
            <span className="font-medium text-foreground/70">{field}:</span>
            <span className="line-through text-red-400/70">{String(value.old || '(vacio)')}</span>
            <span className="text-muted-foreground">-&gt;</span>
            <span className="text-green-400/70">{String(value.new || '(vacio)')}</span>
          </div>
        );
      }
      return null;
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="audit-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-foreground flex items-center gap-2">
              <History className="text-primary" size={24} />
              {t('audit.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('audit.subtitle')}</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-audit">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por MBL, usuario, descripcion..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="audit-search"
            />
          </div>
          <Select value={filterEntity} onValueChange={setFilterEntity}>
            <SelectTrigger className="w-40" data-testid="audit-filter-entity">
              <Filter size={14} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las entidades</SelectItem>
              <SelectItem value="shipment">Embarques</SelectItem>
              <SelectItem value="container">Contenedores</SelectItem>
              <SelectItem value="cost">Costos</SelectItem>
              <SelectItem value="event">Eventos</SelectItem>
              <SelectItem value="note">Notas</SelectItem>
              <SelectItem value="user">Usuarios</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-40" data-testid="audit-filter-action">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las acciones</SelectItem>
              <SelectItem value="created">Creado</SelectItem>
              <SelectItem value="updated">Actualizado</SelectItem>
              <SelectItem value="deleted">Eliminado</SelectItem>
              <SelectItem value="bulk_updated">Masivo</SelectItem>
              <SelectItem value="mbl_changed">MBL Cambiado</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="whitespace-nowrap">
            {filteredLogs.length} registros
          </Badge>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <History size={48} className="mx-auto mb-3 opacity-30" />
            <p>No se encontraron registros</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
            
            <div className="space-y-2">
              {filteredLogs.map((log, i) => {
                const ActionIcon = ACTION_ICONS[log.action] || Edit;
                const EntityIcon = ENTITY_ICONS[log.entity_type] || FileText;
                const actionColorClass = ACTION_COLORS[log.action] || 'bg-muted text-muted-foreground border-border';
                const isShipment = log.entity_type === 'shipment' || log.entity_type === 'container' || log.entity_type === 'cost';

                return (
                  <div key={log.id || i} className="relative pl-12" data-testid={`audit-entry-${i}`}>
                    {/* Timeline dot */}
                    <div className={`absolute left-3 w-5 h-5 rounded-full border flex items-center justify-center ${actionColorClass}`}>
                      <ActionIcon size={10} />
                    </div>

                    <div className="bg-card border border-border rounded-lg p-3 hover:border-primary/30 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <EntityIcon size={14} className="text-muted-foreground flex-shrink-0" />
                            <Badge variant="outline" className="text-xs">
                              {ENTITY_LABELS[log.entity_type] || log.entity_type}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${actionColorClass}`}>
                              {ACTION_LABELS[log.action] || log.action}
                            </Badge>
                            {isShipment && log.entity_id && (
                              <button
                                onClick={() => navigate(`/shipments/${encodeURIComponent(log.entity_id)}`)}
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                                data-testid={`audit-link-${i}`}
                              >
                                {log.entity_id}
                                <ExternalLink size={10} />
                              </button>
                            )}
                          </div>
                          <p className="text-sm mt-1">{log.description}</p>

                          {/* Changes detail */}
                          {log.changes && Object.keys(log.changes).length > 0 && (
                            <div className="mt-2 flex flex-wrap">
                              {formatChanges(log.changes)}
                            </div>
                          )}
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.created_at)}</p>
                          <p className="text-xs text-muted-foreground/70 mt-0.5">
                            {log.user_name || log.user_email}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
